<template>
  <div class="code-block">
    <div class="code-block__header">
      <span class="code-block__language">{{ language }}</span>
      <button
        type="button"
        class="code-block__copy"
        :class="{ 'code-block__copy--success': copyState === 'success' }"
        @click="handleCopy"
      >
        {{ copyLabel }}
      </button>
    </div>
    <pre class="code-block__pre"><code>{{ code }}</code></pre>
  </div>
</template>

<script setup>
import { ref } from 'vue'

const props = defineProps({
  language: { type: String, default: 'text' },
  code: { type: String, required: true },
})

const copyState = ref('idle')

const copyLabel = ref('Copy')

async function handleCopy() {
  if (typeof navigator === 'undefined' || !navigator.clipboard) {
    copyState.value = 'error'
    copyLabel.value = 'Copy unavailable'
    return
  }
  try {
    await navigator.clipboard.writeText(props.code)
    copyState.value = 'success'
    copyLabel.value = 'Copied'
    setTimeout(() => {
      copyState.value = 'idle'
      copyLabel.value = 'Copy'
    }, 1800)
  } catch {
    copyState.value = 'error'
    copyLabel.value = 'Copy failed'
    setTimeout(() => {
      copyState.value = 'idle'
      copyLabel.value = 'Copy'
    }, 2400)
  }
}
</script>

<style scoped>
.code-block {
  margin: 12px 0 16px;
  border: 1px solid var(--shell-border);
  border-radius: 4px;
  background: var(--shell-surface-strong);
  overflow: hidden;
}

.code-block__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 12px;
  border-bottom: 1px solid var(--shell-border);
  background: var(--shell-surface);
  font-family: var(--shell-mono-font);
}

.code-block__language {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--shell-text-muted);
}

.code-block__copy {
  border: 1px solid var(--shell-border);
  background: transparent;
  color: var(--shell-text);
  font-family: var(--shell-mono-font);
  font-size: 0.72rem;
  padding: 4px 10px;
  border-radius: 3px;
  cursor: pointer;
  transition: background 0.1s, color 0.1s, border-color 0.1s;
}

.code-block__copy:hover {
  background: var(--shell-surface-strong);
}

.code-block__copy--success {
  color: var(--status-active, #2c9b66);
  border-color: var(--status-active, #2c9b66);
}

.code-block__pre {
  margin: 0;
  padding: 14px 16px;
  overflow-x: auto;
  font-family: var(--shell-mono-font);
  font-size: 0.85rem;
  line-height: 1.6;
  color: var(--shell-text);
}

.code-block__pre code {
  font-family: inherit;
  background: transparent;
  padding: 0;
  white-space: pre;
}
</style>
